export const isClassLevelInstructor = (cls, instructorId) => {
  if (!cls || instructorId == null) return false
  return cls.instructorId == instructorId || cls.instructor2Id == instructorId
}

export const isSessionDayInstructor = (day, instructorId) => {
  if (!day || instructorId == null) return false
  return day.instructor1Id == instructorId || day.instructor2Id == instructorId
}

export const getClassDays = (cls) => cls?.unfilteredClassDays || cls?.classDays || []

/**
 * Prefer dateStandard when ClassStore has already normalized the day.
 * Fall back to dateFmt with string-based year handling (same approach as ClassStore parseUsDate)
 * so both 2-digit and 4-digit years compare correctly against today.
 */
export const getDayDateStandard = (day) => {
  if (day?.dateStandard) return day.dateStandard
  if (!day?.dateFmt) return null

  const [monthStr, dayStr, yearStrRaw] = `${day.dateFmt}`.split('/')
  if (!monthStr || !dayStr || !yearStrRaw) return null

  const year = yearStrRaw.length === 2 ? Number(`20${yearStrRaw}`) : Number(yearStrRaw)
  const month = Number(monthStr)
  const date = Number(dayStr)
  if (!year || !month || !date) return null

  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(date).padStart(2, '0')}`
}

/**
 * Without includePast: only upcoming assigned sessions count.
 * With includePast: past assigned sessions also count.
 * Site default (flag unset): includePast true; set instructorMyClassesIncludePast false to filter.
 */
export const instructorHasAssignedSession = (days, instructorId, { today, includePast = false } = {}) => {
  return (days || []).some((day) => {
    if (!isSessionDayInstructor(day, instructorId)) return false
    if (includePast) return true
    const dayDate = getDayDateStandard(day)
    return !!dayDate && dayDate >= today
  })
}

/**
 * Whether an open/catalog class should be considered for My Classes.
 * Class-level instructors always match; session instructors match via assigned days.
 */
export const shouldCollectClassForInstructor = (cls, instructorId, { today, includePast = false } = {}) => {
  if (!cls || instructorId == null) return false
  if (isClassLevelInstructor(cls, instructorId)) return true
  return instructorHasAssignedSession(getClassDays(cls), instructorId, { today, includePast })
}

/**
 * Final My Classes list filter after classes are fetched.
 * Without includePast: hide ended classes and session-only rows without an upcoming assignment.
 * With includePast: keep ended classes and past session assignments.
 */
export const shouldIncludeInstructorClass = (cls, instructorId, { today, includePast = false } = {}) => {
  if (!cls || instructorId == null) return false

  // Missing endDateStandard must not count as ended — that was dropping rows
  // while fetchClassById was still formatting the class.
  if (!includePast && cls.endDateStandard && cls.endDateStandard < today) return false

  if (isClassLevelInstructor(cls, instructorId)) return true

  return instructorHasAssignedSession(getClassDays(cls), instructorId, { today, includePast })
}

/**
 * Roster session list for the logged-in instructor.
 * Class-level instructors (or showAllSessions) see every day.
 * Without showAllSessions, session instructors see only days they are assigned to teach.
 * Site default (flag unset): showAllSessions true; set instructorRosterShowAllSessions false to filter.
 */
export const getRosterDaysForInstructor = (days, classDetails, instructorId, { showAllSessions = false } = {}) => {
  const allDays = days || []
  if (!instructorId || !classDetails) return allDays
  if (showAllSessions || isClassLevelInstructor(classDetails, instructorId)) return allDays
  return allDays.filter((day) => isSessionDayInstructor(day, instructorId))
}
